import { log, assert, error } from '../app/logger.js';
import { navigation } from '../app/navigation.js';
import { ComboboxControl } from '../controls/combobox_control.js';
import { HTMLControl } from '../controls/html_control.js';
import { LocationFilter } from '../data/location_filter.js';
import { listenToEvent } from '../util/listen_to_event.js';
import { window } from '../window.js';

import '../controls/popup_button_control.js';

/**
 * A vertical bar of map controls such as close button and switch map type
 * button.
 */
class ControlsBar extends window.HTMLElement {
  static selectors = {
    EnlargeButton: '.map-enlarge-btn',
  };

  static get mapTypeItems() {
    return MapTypeControl.menuitems;
  }
  static get locTypeItems() {
    return LocationFilterControl.menuitems;
  }

  connectedCallback() {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    const defaultControls =
      'close fullscreen maptype locfilter myposition zoom';

    let controls = this.getAttribute('controls') || defaultControls;
    this.innerHTML = controls
      .split(' ')
      .map(control => this.getMarkupFor(control.trim()))
      .reduce((a, v) => (a += v), '');

    this.myPositionEl = this.querySelector('myposition-control');
    this.mapTypeEl = this.querySelector('maptype-control');
    this.locFilterEl = this.querySelector('locfilter-control');

    let closeBtn = this.querySelector('.map-close-btn');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => navigation.closeMap());
    }

    let fsBtn = this.querySelector('.map-fullscreen-btn');
    if (fsBtn) {
      fsBtn.addEventListener('click', window.toggleFullscreen);
    }

    let enlargeBtn = this.querySelector('.map-enlarge-btn');
    if (enlargeBtn) {
      enlargeBtn.addEventListener('click', () =>
        this.dispatchEvent(new Event('enlarge'))
      );
    }

    let zoominBtn = this.querySelector('.map-button-zoomin-button');
    if (zoominBtn) {
      zoominBtn.addEventListener('click', () => (this.map.zoom += 1));
    }
    let zoomoutBtn = this.querySelector('.map-button-zoomout-button');
    if (zoomoutBtn) {
      zoomoutBtn.addEventListener('click', () => (this.map.zoom -= 1));
    }

    let bottomSectionFirstEl = this.myPositionEl || zoominBtn;
    bottomSectionFirstEl.classList.add('map-button-first-in-bottom-block');
  }

  get map() {
    return this._map;
  }
  set map(map) {
    this._map = map;
    if (this.myPositionEl) {
      this.myPositionEl.map = map;
    }
  }

  closePopupIfOpen() {
    return (
      (this.mapTypeEl && this.mapTypeEl.closeIfOpen()) ||
      (this.locFilterEl && this.locFilterEl.closeIfOpen())
    );
  }

  getMarkupFor(control) {
    switch (control) {
      case 'close':
        return `
          <div class='map-close-btn map-bar-button'>
            <img class='autocomplete-button'
                 src='/images/map-button-close.png'
                 title='Close the maps'>
          </div>`;

      case 'fullscreen':
        return `
          <div class='map-fullscreen-btn map-bar-button'>
            <img class='autocomplete-button'
                src='/images/map-button-fullscreen-in.png'
                title='Fullscreen'>
          </div>`;

      case 'enlarge':
        return `
          <div class='map-enlarge-btn map-bar-button'>
            <img class='autocomplete-button'
                src='/images/map-button-enlarge.png'
                title='Switch to Maps'>
          </div>`;

      case 'maptype':
        return `<maptype-control class='map-bar-button'></maptype-control>`;

      case 'locfilter':
        return `<locfilter-control class='map-bar-button'></locfilter-control>`;

      case 'myposition':
        return `
          <myposition-control class='map-bar-button'>
            <img class='autocomplete-button'
                 src='/images/map-myposition.png'
                 title='Show my location'>
          </myposition-control>`;

      case 'zoom':
        return `
          <div class='map-bar-button map-button-zoomin-button'>
            <img class='autocomplete-button'
                src='/images/map-plus-40.png'
                title='Zoom in'>
          </div>
          <div class='map-bar-button-separator'></div>
          <div class='map-bar-button map-button-zoomout-button'>
            <img class='autocomplete-button'
                src='/images/map-subtract-40.png'
                title='Zoom out'>
          </div>`;

      default:
        error(`[map:controls-bar]: no markup for ${control}`);
        return '';
    }
  }
}

/**
 * Switch maps popup button.
 */
class MapTypeControl extends HTMLControl {
  static get menuitems() {
    return [
      {
        img: '/images/map-google-street.png',
        name: 'Street Map',
        title: 'View street map',
        type: 'roadmap',
        onselect() {
          window.map_setType(this.type);
        },
      },
      {
        img: '/images/map-google-satellite.png',
        name: 'Satellite Map',
        title: 'View satellite map',
        type: 'satellite',
        onselect() {
          window.map_setType(this.type);
        },
      },
      {
        img: '/images/map-google-terrain.png',
        name: 'Terrain Map',
        title: 'View topography and elevation',
        type: 'terrain',
        onselect() {
          window.map_setType(this.type);
        },
      },
    ];
  }

  initialize() {
    super.initialize();

    this.popupButton.setItems(this.constructor.menuitems);
    listenToEvent(
      this.popupButton,
      'itemselect',
      ev => ev.detail && ev.detail.onselect()
    );
  }

  static get markup() {
    return `
<popup-button-control name='Maps'
                      img='/images/map-button-geography.png'></popup-button-control>
`;
  }

  get open() {
    return this.popupButton.open;
  }
  set open(val) {
    this.popupButton.open = val;
  }

  closeIfOpen() {
    return this.popupButton.closeIfOpen();
  }

  static selectors = {
    PopupButton: 'popup-button-control',
  };

  static elements = {
    popupButton: this.selectors.PopupButton,
  };
}

/**
 * Filter shown location type button.
 */
class LocationFilterControl extends ComboboxControl {
  static get menuitems() {
    const logid = '[map:locfilter-by-type]';
    return [
      {
        img: '/images/map-world-map.png',
        name: 'Everything',
        title: 'All activities',
        type: 'all',
        onselect() {
          log(`${logid} select.all`);
          window.map_filterLocations(this.type);
        },
      },
      {
        img: '/images/map-sun.png',
        name: 'Day trips',
        title: 'Daily activities',
        type: LocationFilter.DayTrips,
        onselect() {
          log(`${logid} select.daytrips`);
          window.map_filterLocations(this.type);
        },
      },
      {
        img: '/images/map-trailer.png',
        name: 'Frontcountry',
        title: 'Trailers and car camping',
        type: LocationFilter.Frontcountry,
        onselect() {
          log(`${logid} select.frontcountry`);
          window.map_filterLocations(this.type);
        },
      },
      {
        img: '/images/map-bear-footprint.png',
        name: 'Backcountry',
        title: 'Backcountry camping',
        type: LocationFilter.Backcountry,
        onselect() {
          log(`${logid} select.backcountry`);
          window.map_filterLocations(this.type);
        },
      },
    ];
  }

  initialize() {
    super.initialize();

    this.setItems(this.constructor.menuitems);
    listenToEvent(this, 'itemselect', ev => ev.detail && ev.detail.onselect());
  }
}

/**
 * Show my position control.
 */
class MyPositionControl extends window.HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', () => this.showPosition());
  }

  get map() {
    return this._map;
  }
  set map(map) {
    this._map = map;
    this.marker = new window.Marker({
      name: 'myposition-marker',
      map,
      clickable: false,
      icon: {
        url: '/images/map-myposition-marker.png',
        size: { width: 32, height: 32 },
        anchor: { x: 16, y: 16 },
      },
    });
  }

  showPosition() {
    assert(this.map, `MyPositionControl: no map set`);
    navigator.geolocation.getCurrentPosition(
      pos => {
        let loc = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        this.marker.position = loc;
        this.marker.visible = true;
        this.map.panTo(loc);
        this.map.zoom = 16;
      },
      e => error(e)
    );
  }

  hidePosition() {
    this.marker.visible = true;
  }
}

window.customElements.define('map-controlsbar', ControlsBar);
window.customElements.define('maptype-control', MapTypeControl);
window.customElements.define('locfilter-control', LocationFilterControl);
window.customElements.define('myposition-control', MyPositionControl);

export { ControlsBar };
